import {
  useQuery as useOriginalQuery,
  useLazyQuery as useOriginalLazyQuery,
  OperationVariables,
} from '@apollo/client'
import { DocumentNode } from 'graphql'
import { QueryResult, QueryHookOptions } from './useQuery.types'

import { captureCacheMiss } from './captureCacheMiss'
import { useShouldInvalidatePrevious } from './useShouldInvalidatePrevious'
import { removeDirectivesFromDocument } from '@apollo/client/utilities'

// TODO remove @client directives, see: https://sketch.slack.com/archives/C09BF8KAXB8/p1756819151670889
const testDocsCache = new WeakMap<DocumentNode, DocumentNode>()
const removeClientDirectives = (doc: DocumentNode) => {
  if (testDocsCache.has(doc)) {
    return testDocsCache.get(doc)!
  }
  const newDoc = removeDirectivesFromDocument([{ name: 'client' }], doc) || doc
  testDocsCache.set(doc, newDoc)
  return newDoc
}

export function useQuery<
  Data = any,
  Variables extends OperationVariables = OperationVariables,
>(
  _query: DocumentNode,
  options?: QueryHookOptions<Data, Variables>
): QueryResult<Data, Variables> {
  // just omit shouldInvalidatePrevious prop from passing props forward
  const { shouldInvalidatePrevious, fetchPolicy, ...restOptions } =
    options || {}

  // TODO remove @client directives, see: https://sketch.slack.com/archives/C09BF8KAXB8/p1756819151670889
  const query =
    process.env.NODE_ENV === 'test' &&
    process.env.REMOVE_CLIENT_DIRECTIVE === 'true'
      ? removeClientDirectives(_query)
      : _query

  const result = useOriginalQuery<Data, Variables>(query, {
    ...restOptions,
    fetchPolicy,
  })

  captureCacheMiss(query, options, result)

  if (useShouldInvalidatePrevious<Data, Variables>(options, result)) {
    Object.assign(result, { data: undefined, error: undefined })
  }

  return result as QueryResult<Data, Variables>
}

export const useLazyQuery: typeof useOriginalLazyQuery = (query, options) => {
  return useOriginalLazyQuery(query, options)
}
